/**
 * AppPromoSlidesAdmin — Super Admin editor for the /app landing carousel.
 * Lives inside the existing Admin dashboard as its own tab/section.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Save, ArrowUp, ArrowDown, Eye, EyeOff, Image as ImageIcon, Link as LinkIcon } from "lucide-react";
import FileUpload from "@/components/churchbuilder/FileUpload";

const inp = "w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-3 py-2.5 rounded text-sm text-white";

const blank = () => ({ image_url: "", link_url: "https://link-my.app/hohvibes-studio", alt: "", label: "", order: 0, active: true });

export default function AppPromoSlidesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/app-promo-slides");
      setItems(data);
    } catch { toast.error("Could not load slides"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (expanded) load(); }, [expanded]);

  const startNew = () => { setEditingId("new"); setForm({ ...blank(), order: items.length }); };
  const startEdit = (s) => { setEditingId(s.id); setForm({ image_url: s.image_url, link_url: s.link_url || "", alt: s.alt || "", label: s.label || "", order: s.order || 0, active: !!s.active }); };
  const cancel = () => { setEditingId(null); setForm(blank()); };

  const save = async () => {
    if (!form.image_url) return toast.error("Image required");
    setSaving(true);
    try {
      if (editingId === "new") {
        await api.post("/admin/app-promo-slides", form);
        toast.success("Slide added");
      } else {
        await api.patch(`/admin/app-promo-slides/${editingId}`, form);
        toast.success("Slide updated");
      }
      cancel();
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/admin/app-promo-slides/${s.id}`, {
        image_url: s.image_url, link_url: s.link_url, alt: s.alt, label: s.label, order: s.order, active: !s.active,
      });
      load();
    } catch { toast.error("Toggle failed"); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    try {
      await api.post("/admin/app-promo-slides/reorder", { ids: arr.map((s) => s.id) });
    } catch { toast.error("Reorder failed"); load(); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this slide?")) return;
    try {
      await api.delete(`/admin/app-promo-slides/${id}`);
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="app-promo-admin" className="luxury-card rounded-xl p-5 mt-6 border border-[#D4AF37]/15">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <ImageIcon size={16} className="text-[#D4AF37]" />
          <div>
            <div className="font-serif-display text-xl">App Landing Carousel</div>
            <div className="text-xs text-neutral-400 mt-0.5">Manage the promotional slides shown at <span className="font-mono">/app</span>. Add seasonal campaigns and disable them when they end.</div>
          </div>
        </div>
        <button
          type="button"
          data-testid="app-promo-toggle"
          onClick={() => setExpanded((p) => !p)}
          className="btn-ghost-gold px-3 py-2 rounded text-[10px] tracking-widest uppercase"
        >{expanded ? "Hide" : "Manage slides"}</button>
      </div>

      {expanded && (
        <div className="mt-5 space-y-4">
          {!editingId && (
            <button data-testid="app-promo-new" onClick={startNew} className="btn-gold px-4 py-2 rounded text-[10px] tracking-widest uppercase font-semibold inline-flex items-center gap-2">
              <Plus size={12} /> Add slide
            </button>
          )}

          {editingId && (
            <div className="rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5 space-y-3" data-testid="app-promo-form">
              <div>
                <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-1.5 flex items-center gap-2"><ImageIcon size={11} /> Promo image (portrait works best · aspect ratio preserved)</div>
                <FileUpload value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} accept="image/*" label="Slide image" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-1.5">Campaign label (small overlay text)</div>
                  <input data-testid="app-promo-label" className={inp} placeholder="e.g. Christmas Campaign" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
                </div>
                <div>
                  <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-1.5 flex items-center gap-2"><LinkIcon size={11} /> Destination link</div>
                  <input data-testid="app-promo-link" className={inp} placeholder="https://link-my.app/hohvibes-studio" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} />
                </div>
              </div>
              <div>
                <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-1.5">Alt text (accessibility)</div>
                <input className={inp} placeholder="What is shown on the poster?" value={form.alt} onChange={(e) => setForm({ ...form, alt: e.target.value })} />
              </div>
              <label className="inline-flex items-center gap-2 text-xs text-neutral-300">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active (show on the public site)
              </label>
              <div className="flex gap-2 pt-2">
                <button data-testid="app-promo-save" disabled={saving} onClick={save} className="btn-gold px-4 py-2 rounded text-[10px] tracking-widest uppercase font-semibold inline-flex items-center gap-2">
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save slide
                </button>
                <button onClick={cancel} className="btn-ghost-gold px-4 py-2 rounded text-[10px] tracking-widest uppercase">Cancel</button>
              </div>
            </div>
          )}

          {loading ? <Loader2 className="animate-spin text-[#D4AF37]" size={16} /> :
            items.length === 0 ? <div className="text-xs text-neutral-500">No slides yet.</div> :
            <div className="space-y-2">
              {items.map((s, i) => (
                <div key={s.id} data-testid={`app-promo-row-${s.id}`} className={`rounded-lg border p-3 flex items-center gap-4 ${s.active ? "border-white/10 bg-white/[0.02]" : "border-white/5 bg-black/40 opacity-60"}`}>
                  <img src={s.image_url} alt={s.alt || s.label} className="w-16 h-20 object-cover rounded border border-white/10" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white">{s.label || "(untitled slide)"}</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5 truncate">{s.link_url || "no link"}</div>
                    <div className="text-[10px] text-neutral-500 mt-0.5">order {i + 1} · {s.active ? "active" : "hidden"}</div>
                  </div>
                  <div className="flex flex-wrap gap-1 shrink-0">
                    <button data-testid={`app-promo-up-${s.id}`} title="Move up" onClick={() => move(i, -1)} disabled={i === 0} className="text-neutral-400 hover:text-[#D4AF37] disabled:opacity-30 p-2"><ArrowUp size={13} /></button>
                    <button data-testid={`app-promo-down-${s.id}`} title="Move down" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-neutral-400 hover:text-[#D4AF37] disabled:opacity-30 p-2"><ArrowDown size={13} /></button>
                    <button data-testid={`app-promo-toggle-${s.id}`} title={s.active ? "Hide" : "Show"} onClick={() => toggleActive(s)} className="text-neutral-400 hover:text-[#D4AF37] p-2">
                      {s.active ? <Eye size={13} /> : <EyeOff size={13} />}
                    </button>
                    <button data-testid={`app-promo-edit-${s.id}`} onClick={() => startEdit(s)} className="text-[#D4AF37] text-[10px] tracking-widest uppercase px-3">Edit</button>
                    <button data-testid={`app-promo-delete-${s.id}`} onClick={() => remove(s.id)} className="text-neutral-400 hover:text-red-400 p-2"><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
            </div>
          }
        </div>
      )}
    </div>
  );
}
